// PPE page reads — the issuable-item list, the issue register, what has been
// resolved against each issue, and the names of items the cached list lost.
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { inChunks } from '@/lib/inChunks'
import type { PpeAckMethod } from './ppeIssueDraft'
import type { ItemLookup, RegisterIssue, Resolved } from './ppeRegister'

export interface IssuableItem {
  item_id:      string
  item:         string
  size:         string | null
  category:     string | null
  on_hand:      number
  price:        number | null
  shared_from:  string | null   // custodian name when it came from a shared store
}

interface PpeIssueRow extends RegisterIssue {
  acknowledged_method: PpeAckMethod
  witness_name:        string | null
}

// Everything this tenant can issue: its own stock plus anything shared into it.
// Both reads are already tenant-correct server-side.
//
// The select and the row shaping are exported because the offline prewarm
// (lib/offlineLookups.ts 'ppe-issuable-items') fills THIS query's cache entry
// and must never drift from it (FM07 UF-18: the Issue sheet works with no signal
// on the lists it prepared).
export const PPE_OWN_STOCK_SELECT = 'item_id, item, size, category_name, current_quantity, unit_price_zar, cost_price_zar'

type SharedStockRow = {
  item_id: string; item: string; size: string | null; category_name?: string | null
  current_quantity: number; cost_price_zar: number | null
  recovery_price_zar: number | null; custodian_name: string
}
type OwnStockRow = {
  item_id: string; item: string; size: string | null; category_name?: string | null
  current_quantity: number | string; unit_price_zar: number | null; cost_price_zar: number | null
}

export function shapeIssuableItems(
  ownRows: readonly OwnStockRow[], sharedRows: readonly SharedStockRow[], sharedFailed: boolean,
): { items: IssuableItem[]; sharedFailed: boolean } {
  const mine: IssuableItem[] = ownRows.map(r => ({
    item_id: r.item_id, item: r.item, size: r.size, category: r.category_name ?? null,
    on_hand: Number(r.current_quantity),
    price: r.unit_price_zar ?? r.cost_price_zar, shared_from: null,
  }))
  // M145 — the price shown at signing MUST be the one post_ppe_issue
  // snapshots into unit_cost_zar, or the worker signs for a figure they are
  // never charged. Own items already match (unit_price_zar ?? cost_price_zar
  // == the RPC's COALESCE). The shared RPC returns the already-coalesced
  // `recovery_price_zar`: use it as-is. Do NOT re-derive it here.
  const theirs: IssuableItem[] = (sharedFailed ? [] : sharedRows).map(r => ({
    item_id: r.item_id, item: r.item, size: r.size, category: r.category_name ?? null,
    on_hand: Number(r.current_quantity),
    price: r.recovery_price_zar ?? r.cost_price_zar, shared_from: r.custodian_name,
  }))
  return { items: [...mine, ...theirs], sharedFailed }
}

export function useIssuableItems(tenantId: string | null) {
  return useQuery({
    queryKey: ['ppe-issuable-items', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const own = await supabase.from('stock_balances')
        .select(PPE_OWN_STOCK_SELECT)
        .eq('tenant_id', tenantId).is('deleted_at', null).order('item')
      if (own.error) throw own.error

      // A failed shared-store read must not throw away the farm's OWN items:
      // keep them, and say the shared half is missing.
      const shared = await withReadTimeout(supabase.rpc('get_shared_stock_balances'), 'FarmPpe: get_shared_stock_balances')
      return shapeIssuableItems(own.data ?? [], (shared.data ?? []) as SharedStockRow[], !!shared.error)
    },
  })
}

export function usePpeIssues(tenantId: string | null) {
  return useQuery({
    queryKey: ['ppe-issues', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      // M147 — the header tiles and the owing figure derive from THIS list, so a
      // cap on it is a cap on the liability. Drain it properly; maxRows is a
      // runaway backstop, not a display cap.
      const { rows, error } = await fetchAllPaged<PpeIssueRow>(
        (from, to) => supabase.from('ppe_issues')
          .select('issue_id, worker_number, worker_name, quantity, size, unit_cost_zar, acknowledged_method, witness_name, responsibility_state, created_at, item_id, version')
          .eq('tenant_id', tenantId).is('deleted_at', null)
          .order('created_at', { ascending: false }).order('issue_id')
          .range(from, to),
        { maxRows: 20_000 },
      )
      if (error) throw new Error(error)
      return rows
    },
  })
}

/** What has been resolved against each issue: units, and the charge the server
 *  recorded — the other half of "how many are still out" (UF-15), and the only
 *  honest source for "owing" (a part return shrinks a Lost charge). */
export function usePpeResolved(tenantId: string | null) {
  return useQuery({
    queryKey: ['ppe-resolutions', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { rows, error } = await fetchAllPaged<{ issue_id: string; quantity: number; charge_amount_zar: number | null }>(
        (from, to) => supabase.from('ppe_issue_resolutions')
          .select('issue_id, quantity, charge_amount_zar')
          .eq('tenant_id', tenantId)
          .order('created_at', { ascending: true }).order('resolution_id')
          .range(from, to),
        { maxRows: 50_000 },
      )
      if (error) throw new Error(error)
      const sums = new Map<string, Resolved>()
      for (const r of rows) {
        const cur = sums.get(r.issue_id) ?? { qty: 0, charge: 0 }
        sums.set(r.issue_id, { qty: cur.qty + Number(r.quantity), charge: cur.charge + Number(r.charge_amount_zar ?? 0) })
      }
      return sums as ReadonlyMap<string, Resolved>
    },
  })
}

type NamedItem = { item: string; size: string | null }

/** item_id → what the register can say about it. Known (cached issuable list) →
 *  its row. Otherwise ONE read of stock_items: found → its row, absent or
 *  deleted → 'gone'. While the list is loading nothing is looked up and the
 *  answer is undefined (not known yet). When the list FAILED, every id is
 *  looked up instead. If only the shared half failed, an id the lookup cannot
 *  see stays undefined: it may be a shared item we cannot read. */
export function useItemNames(
  issues: readonly RegisterIssue[],
  itemsData: { items: readonly IssuableItem[]; sharedFailed: boolean } | undefined,
  itemsFailed: boolean,
): (id: string) => ItemLookup {
  const known = useMemo(() => new Map<string, NamedItem>((itemsData?.items ?? []).map(i => [i.item_id, i])), [itemsData])
  const missing = useMemo(() => {
    if (!itemsData && !itemsFailed) return []
    return [...new Set(issues.map(i => i.item_id))].filter(id => !known.has(id)).sort()
  }, [issues, itemsData, itemsFailed, known])
  const extraQ = useQuery({
    queryKey: ['ppe-item-names', missing],
    enabled: missing.length > 0,
    queryFn: async () => {
      const out: Array<{ item_id: string; item: string; size: string | null; deleted_at: string | null }> = []
      for (const ids of inChunks(missing)) {
        const { data, error } = await withReadTimeout(
          supabase.from('stock_items').select('item_id, item, size, deleted_at').in('item_id', ids),
          'FarmPpe: stock_items names')
        if (error) throw error
        out.push(...(data ?? []))
      }
      return out
    },
  })
  return useMemo(() => {
    const found = new Map((extraQ.data ?? []).map(r => [r.item_id, r]))
    // "Gone" only when BOTH halves of the item read landed: a failed own read
    // (no itemsData) or a missing shared half leaves shared-store items the
    // stock_items lookup cannot see (own-tenant RLS), which are not gone.
    const canSayGone = extraQ.isSuccess && !!itemsData && !itemsData.sharedFailed
    return (id: string): ItemLookup => {
      const k = known.get(id)
      if (k) return k
      const r = found.get(id)
      if (r) return r.deleted_at ? 'gone' : r
      return canSayGone && missing.includes(id) ? 'gone' : undefined
    }
  }, [known, extraQ.data, extraQ.isSuccess, itemsData, missing])
}
